const { marked } = require('marked');
const defaultIconUrl = 'https://images.near.st/channels/default.svg';

/**
 * Abstract class for channel definitions.
 *
 * @abstract
 */
class ChannelDefinition {
	/**
	 * The ID of the channel.
	 * @type {string}
	 */
	id;

	/**
	 * The issues channel ID of the channel.
	 * @type {string}
	 * @default this.id
	 */
	issuesChannelId;

	/**
	 * The title of the channel.
	 * @type {string}
	 */
	title;

	/**
	 * The description of the channel.
	 * @type {string}
	 */
	description;

	/**
	 * The category of the channel.
	 * @type {{
	 * 	title: string,
	 * 	description: string
	 * }}
	 */
	category;

	/**
	 * The long description of the channel.
	 * @type {string}
	 */
	longDescription;

	/**
	 * The icon url of the channel.
	 * @type {string}
	 */
	iconUrl;

	/**
	 * The cover image url of the channel.
	 * @type {string}
	 */
	coverImageUrl;

	/**
	 * The channels this channel works with.
	 * @type {string[]}
	 */
	worksWith;

	/**
	 * The help article urls of the channel.
	 * @type {{
	 * 	title: string,
	 * 	url: string
	 * }[]}
	 */
	helpArticleUrls;

	/**
	 * The url of the channel's manage page.
	 * @type {string}
	 */
	manageUrl;

	/**
	 * Whether the channel has product specific visibility.
	 * @type {boolean}
	 */
	productSpecificVisibility;

	/**
	 * The requirements of the channel.
	 * @type {import('./Requirements')}
	 */
	requirements;

	/**
	 * Whether the channel is coming soon.
	 * @type {boolean}
	 */
	comingSoon = false;

	/**
	 * Optional tag to display on the channel card and header (e.g. "Beta", "New").
	 * @type {string|undefined}
	 */
	tag;

	/**
	 * Whether the channel is set up by our team on request, so the dashboard
	 * offers "Register interest" instead of a self-serve Enable.
	 * @type {boolean}
	 */
	registerInterest = false;

	/**
	 * Whether the channel is available in the partner API.
	 * @type {boolean}
	 */
	isAvailableInPartnerApi = false;

	/**
	 * Whether the channel has a feed export option.
	 * @type {boolean}
	 */
	feedExport = false;

	/**
	 * Whether the channel supplements its own product data (titles, descriptions, images).
	 * Channels that supplement don't need unenriched items filtered out before syncing.
	 * @type {boolean}
	 */
	supplementsProductData = false;

	/**
	 * HTML version of markdown long description.
	 */
	get longDescriptionHtml() {
		if (!this.longDescription) return '';

		return marked.parse(this.longDescription);
	}

	toJSON() {
		return {
			id: this.id,
			issuesChannelId: this.issuesChannelId || this.id,
			title: this.title,
			description: this.description,
			longDescription: this.longDescription,
			longDescriptionHtml: this.longDescriptionHtml,
			iconUrl: this.iconUrl || defaultIconUrl,
			category: this.category?.title,
			categorySubtitle: this.category?.description,
			coverImageUrl: this.coverImageUrl,
			worksWith: this.worksWith,
			helpArticleUrls: this.helpArticleUrls,
			manageUrl: this.manageUrl,
			productSpecificVisibility: this.productSpecificVisibility,
			requirements: this.requirements,
			comingSoon: this.comingSoon,
			tag: this.tag,
			feedExport: this.feedExport,
			supplementsProductData: this.supplementsProductData,
			registerInterest: this.registerInterest
		};
	}
}

module.exports = ChannelDefinition;
